import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Sidebar from "components/Sidebar/Sidebar";
import StorageUsage from "components/StorageUsage";
import {
  fetchStorageSummary,
  requestArchiveCode,
  requestExtraStorage,
  verifyArchiveCode,
} from "services/storageService/storageService";
import { StorageSummary } from "services/storageService/storageService.types";
import {
  ActionButton,
  Actions,
  CategoryCard,
  CategoryGrid,
  CategoryName,
  CategoryValue,
  CodeInput,
  Content,
  ErrorText,
  FeatureCard,
  FeatureTitle,
  Header,
  Modal,
  ModalActions,
  ModalBackdrop,
  ModalTitle,
  Note,
  Page,
  RequestFields,
  RequestInput,
  RequestNotice,
  RequestSelect,
  Subtitle,
  Title,
  TitleBlock,
  UsageCard,
} from "./StoragePage.styles";

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 ГБ";
  const gib = bytes / 1024 ** 3;
  if (gib >= 1) return `${gib.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ГБ`;
  return `${(bytes / 1024 ** 2).toLocaleString("ru-RU", { maximumFractionDigits: 0 })} МБ`;
};

const formatRemaining = (days: number) => {
  if (days < 31) return `${days} ${days % 10 === 1 && days % 100 !== 11 ? "день" : [2, 3, 4].includes(days % 10) && ![12, 13, 14].includes(days % 100) ? "дня" : "дней"}`;
  const months = Math.max(1, Math.floor(days / 30));
  return `${months} ${months % 10 === 1 && months % 100 !== 11 ? "месяц" : [2, 3, 4].includes(months % 10) && ![12, 13, 14].includes(months % 100) ? "месяца" : "месяцев"}`;
};

const StoragePage: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<StorageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [retryAfter, setRetryAfter] = useState(0);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestAmount, setRequestAmount] = useState("1");
  const [requestUnit, setRequestUnit] = useState<"MB" | "GB">("GB");
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    const refresh = () => { fetchStorageSummary().then(setSummary).catch(() => setError("Не удалось обновить данные хранилища.")).finally(() => setLoading(false)); };
    refresh();
    window.addEventListener("storage-usage-changed", refresh);
    return () => window.removeEventListener("storage-usage-changed", refresh);
  }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("request") === "extra") setRequestModalOpen(true);
  }, []);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = window.setInterval(() => setRetryAfter((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [retryAfter]);

  const categories = useMemo(() => summary ? [
    ["Материалы курсов", summary.categories.files],
    ["Ответы учеников", summary.categories.student_answers],
    ["Сайт школы", summary.categories.site],
    ["Архив", summary.categories.archive],
  ] as const : [], [summary]);

  const openArchive = async () => {
    setError("");
    setArchiveModalOpen(true);
    try {
      const result = await requestArchiveCode();
      setRetryAfter(result.retry_after || 0);
    } catch (requestError: any) {
      setRetryAfter(requestError?.response?.data?.retry_after || 0);
      setError(requestError?.response?.data?.detail || "Не удалось отправить код.");
    }
  };

  const verifyCode = async () => {
    setError("");
    try {
      await verifyArchiveCode(code);
      setArchiveModalOpen(false);
      setCode("");
      navigate("/storage/archive");
    } catch (requestError: any) {
      setError(requestError?.response?.data?.detail || "Код не подошёл.");
      setRetryAfter(requestError?.response?.data?.retry_after || 0);
    }
  };

  const sendExtraStorageRequest = async () => {
    const amount = Number(requestAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Укажите объём больше нуля.");
      return;
    }
    const multiplier = requestUnit === "GB" ? 1024 ** 3 : 1024 ** 2;
    setError("");
    try {
      await requestExtraStorage(Math.round(amount * multiplier));
      setRequestSent(true);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.requested_bytes?.[0] || "Не удалось отправить запрос.");
    }
  };

  return (
    <Page>
      <Sidebar />
      <Content>
        <Header>
          <TitleBlock>
            <Title>Хранилище</Title>
            <Subtitle>Материалы курсов, ответы учеников, сайт школы и архив</Subtitle>
          </TitleBlock>
          <Actions>
            {summary?.can_upgrade ? <ActionButton onClick={() => navigate("/payment")}>Перейти на другой план</ActionButton> : null}
            {summary?.can_request_extra ? <ActionButton onClick={() => { setError(""); setRequestSent(false); setRequestModalOpen(true); }}>Запросить дополнительное место</ActionButton> : null}
            <ActionButton $primary onClick={() => void openArchive()}>Открыть архив</ActionButton>
          </Actions>
        </Header>

        <StorageUsage />
        <UsageCard aria-busy={loading}>
          {error && !archiveModalOpen && !requestModalOpen ? <ErrorText role="alert">{error}</ErrorText> : null}
          <CategoryGrid>
            {categories.map(([name, value]) => (
              <CategoryCard key={name}>
                <CategoryName>{name}</CategoryName>
                <CategoryValue>{formatBytes(value)}</CategoryValue>
              </CategoryCard>
            ))}
          </CategoryGrid>
          <Note>
            Архивация курса не отключает доступ учеников. Она переносит курс из рабочего списка школы в защищённый раздел. Остановка доступа учеников выполняется отдельным действием.
          </Note>
        </UsageCard>

        {summary?.subscription?.end_date ? (
          <FeatureCard>
            <FeatureTitle>Срок действия тарифа</FeatureTitle>
            <Subtitle>
              Осталось: {formatRemaining(summary.subscription.days_remaining)}. Дата окончания: {new Date(summary.subscription.end_date).toLocaleDateString("ru-RU")}.
            </Subtitle>
          </FeatureCard>
        ) : null}

      </Content>

      {archiveModalOpen ? (
        <ModalBackdrop role="presentation">
          <Modal role="dialog" aria-modal="true" aria-labelledby="archive-title">
            <ModalTitle id="archive-title">Подтверждение по почте</ModalTitle>
            <Subtitle>Мы отправили шестизначный код на почту школы. Код действует 5 минут, а новый код можно запросить сразу. Содержимое архива появится только после проверки.</Subtitle>
            <CodeInput
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Код из письма"
            />
            {error ? <ErrorText>{error}</ErrorText> : null}
            <ModalActions>
              <ActionButton onClick={() => setArchiveModalOpen(false)}>Отмена</ActionButton>
              <ActionButton onClick={() => void openArchive()}>Получить новый код</ActionButton>
              <ActionButton $primary disabled={code.length !== 6} onClick={() => void verifyCode()}>Открыть</ActionButton>
            </ModalActions>
          </Modal>
        </ModalBackdrop>
      ) : null}
      {requestModalOpen ? (
        <ModalBackdrop role="presentation">
          <Modal role="dialog" aria-modal="true" aria-labelledby="request-storage-title">
            <ModalTitle id="request-storage-title">Запрос дополнительного места</ModalTitle>
            {requestSent ? (
              <Subtitle>Запрос отправлен. Администратор увидит вашу почту, текущий остаток и запрошенный объём.</Subtitle>
            ) : (
              <>
                <Subtitle>Укажите нужный объём в МБ или ГБ.</Subtitle>
                <RequestFields>
                  <RequestInput type="number" min="0.01" step="0.01" value={requestAmount} onChange={(event) => setRequestAmount(event.target.value)} aria-label="Объём" />
                  <RequestSelect value={requestUnit} onChange={(event) => setRequestUnit(event.target.value as "MB" | "GB")} aria-label="Единица измерения">
                    <option value="MB">МБ</option>
                    <option value="GB">ГБ</option>
                  </RequestSelect>
                </RequestFields>
                <RequestNotice>Дополнительные гигабайты предоставляются на платной основе. Стоимость и объём согласовываются после рассмотрения запроса.</RequestNotice>
              </>
            )}
            {error ? <ErrorText>{error}</ErrorText> : null}
            <ModalActions>
              <ActionButton onClick={() => setRequestModalOpen(false)}>{requestSent ? "Закрыть" : "Отмена"}</ActionButton>
              {!requestSent ? <ActionButton $primary onClick={() => void sendExtraStorageRequest()}>Отправить запрос</ActionButton> : null}
            </ModalActions>
          </Modal>
        </ModalBackdrop>
      ) : null}
    </Page>
  );
};

export default StoragePage;
